// Zero-dependency cumulative-equity line chart. One polyline per source.
// Deliberately no charting lib — the codebase is dependency-light and this
// is a monotone-in-x cumsum, not a chart that needs axes/zoom/brushing.
// Labels live in normal DOM (not the SVG) so `preserveAspectRatio="none"`
// stretching never squishes glyphs.

import type { JournalSource } from "../api/types";
import { usd } from "../lib/format";

export interface EquitySeries {
  source: JournalSource;
  points: { t: number; cum: number }[];
}

interface Props {
  series: EquitySeries[];
  height?: number;
}

const W = 1000;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 10;
const PAD_B = 10;

function sourceColor(s: JournalSource): string {
  if (s === "sim") return "var(--accent-purple)";
  if (s === "live") return "var(--accent)";
  return "var(--fg-dim)";
}

export function EquityCurve({ series, height = 220 }: Props) {
  const all = series.flatMap((s) => s.points);

  if (all.length === 0) {
    return (
      <div className="card">
        <h2>Equity curve <span className="muted small">— cumulative net, by source</span></h2>
        <div className="muted small">no closed trades in this window yet</div>
      </div>
    );
  }

  const ts = all.map((p) => p.t);
  const ys = all.map((p) => p.cum).concat([0]); // always include the 0 baseline
  const minT = Math.min(...ts);
  const maxT = Math.max(...ts);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const innerW = W - PAD_L - PAD_R;
  const innerH = height - PAD_T - PAD_B;
  const sx = (t: number) => PAD_L + ((t - minT) / ((maxT - minT) || 1)) * innerW;
  const sy = (v: number) => PAD_T + (1 - (v - minY) / ((maxY - minY) || 1)) * innerH;

  return (
    <div className="card">
      <h2>Equity curve <span className="muted small">— cumulative net, by source</span></h2>
      <div className="row wrap small" style={{ marginBottom: 6 }}>
        {series.map((s) => (
          <span key={s.source} style={{ color: sourceColor(s.source) }}>
            {s.source === "sim" ? "sim" : s.source}: <span className="mono">{usd(s.points.at(-1)?.cum ?? 0, { sign: true })}</span>
          </span>
        ))}
        <div className="spacer" />
        <span className="muted mono">
          peak {usd(maxY, { sign: true })} · trough {usd(minY, { sign: true })}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
        style={{ display: "block" }}
      >
        <line
          x1={PAD_L}
          x2={W - PAD_R}
          y1={sy(0)}
          y2={sy(0)}
          stroke="var(--border)"
          strokeDasharray="4 4"
          vectorEffect="non-scaling-stroke"
        />
        {series.map((s) => (
          <polyline
            key={s.source}
            fill="none"
            stroke={sourceColor(s.source)}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            points={s.points.map((p) => `${sx(p.t)},${sy(p.cum)}`).join(" ")}
          />
        ))}
      </svg>
    </div>
  );
}
